// Scatters the merchant's Christmas line-art (CSS sprites cropped from two sheets, described by the
// JSON config inside <s-doodle-field>) over the WHOLE page on a jittered grid, so the doodles scroll
// with the content, then brings each one to life:
//   wrapper → position + resting tilt
//   floater → endless slow drift + breathe (WAAPI, paused while off-screen)
//   actor   → a random one-shot action every few seconds (wave, bounce, wiggle, swing, spin, pop)
// prefers-reduced-motion: doodles stay put, no animation.
const ACTIONS = {
  wave: { duration: 1400, keyframes: [{ rotate: '0deg' }, { rotate: '-12deg' }, { rotate: '10deg' }, { rotate: '-8deg' }, { rotate: '6deg' }, { rotate: '0deg' }] },
  wiggle: { duration: 900, keyframes: [{ translate: '0 0' }, { translate: '-5px 0' }, { translate: '5px 0' }, { translate: '-3px 0' }, { translate: '3px 0' }, { translate: '0 0' }] },
  bounce: { duration: 1100, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', keyframes: [
    { translate: '0 0', scale: '1 1' }, { translate: '0 2px', scale: '1.08 0.92', offset: 0.15 },
    { translate: '0 -18px', scale: '0.95 1.06', offset: 0.45 }, { translate: '0 0', scale: '1.05 0.95', offset: 0.75 }, { translate: '0 0', scale: '1 1' },
  ] },
  swing: { duration: 1800, keyframes: [{ rotate: '0deg' }, { rotate: '16deg' }, { rotate: '-13deg' }, { rotate: '9deg' }, { rotate: '-5deg' }, { rotate: '0deg' }], origin: '50% 0%' },
  spin: { duration: 1600, easing: 'cubic-bezier(0.32, 0.72, 0, 1)', keyframes: [{ rotate: '0deg', scale: '1' }, { rotate: '180deg', scale: '1.15' }, { rotate: '360deg', scale: '1' }] },
  pop: { duration: 800, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', keyframes: [{ scale: '1' }, { scale: '1.22', offset: 0.35 }, { scale: '0.94', offset: 0.7 }, { scale: '1' }] },
};

class SDoodleField extends HTMLElement {
  connectedCallback() {
    try {
      this.config = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '{}');
    } catch {
      return;
    }
    if (!this.config.doodles?.length) return;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.layer = document.createElement('div');
    this.layer.className = 'absolute inset-0';
    this.appendChild(this.layer);
    this.actors = [];
    this.visibleActors = new Set();

    // Only doodles near the viewport animate; the rest pause to keep scrolling smooth.
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const actor = entry.target.doodleActor;
          if (!actor) continue;
          if (entry.isIntersecting) {
            this.visibleActors.add(actor);
            actor.float?.play();
          } else {
            this.visibleActors.delete(actor);
            actor.float?.pause();
          }
        }
      },
      { rootMargin: '200px 0px' }
    );

    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (Math.abs(window.innerWidth - this.builtWidth) > 40) this.build();
        else this.extend();
      }, 200);
    };
    window.addEventListener('resize', this.onResize, { passive: true });
    // Page height changes as images and sections load: grow the field to match.
    this.bodyObserver = new ResizeObserver(() => this.onResize());
    this.bodyObserver.observe(document.body);

    this.onMotionChange = () => this.build();
    this.reducedMotion.addEventListener('change', this.onMotionChange);
    this.onVisibility = () => (document.hidden ? this.stopActions() : this.startActions());
    document.addEventListener('visibilitychange', this.onVisibility);
    this.build();
  }

  disconnectedCallback() {
    this.stopActions();
    this.observer?.disconnect();
    this.bodyObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    this.reducedMotion?.removeEventListener('change', this.onMotionChange);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  // Height of the page content, measured without this layer so it can't inflate itself.
  pageHeight() {
    let bottom = window.innerHeight;
    for (const child of document.body.children) {
      if (child === this || child.tagName === 'SCRIPT') continue;
      const rect = child.getBoundingClientRect();
      if (rect.height) bottom = Math.max(bottom, rect.bottom + window.scrollY);
    }
    return Math.ceil(bottom);
  }

  // Shuffled deck so every element of both sheets appears before any repeats.
  nextDoodle() {
    if (!this.deck?.length) {
      this.deck = [...this.config.doodles].sort(() => Math.random() - 0.5);
    }
    return this.deck.pop();
  }

  build() {
    this.stopActions();
    this.observer.disconnect();
    this.layer.replaceChildren();
    this.actors = [];
    this.visibleActors.clear();
    this.deck = [];

    const width = document.documentElement.clientWidth;
    const mobile = width < 768;
    this.builtWidth = window.innerWidth;
    this.cell = mobile ? 145 : 260;
    this.cols = Math.max(3, Math.round(width / this.cell));
    this.cellW = width / this.cols;
    this.baseSize = mobile ? 50 : 72;
    this.rowsBuilt = 0;
    this.extend();
    this.startActions();
  }

  // Adds rows until the field covers the page; when the page shrinks, the extra rows are just hidden.
  extend() {
    const height = this.pageHeight();
    this.style.height = `${height}px`;
    const rowsNeeded = Math.ceil(height / this.cell);
    for (let row = this.rowsBuilt; row < rowsNeeded; row++) {
      for (let col = 0; col < this.cols; col++) {
        if (Math.random() < 0.15) continue; // a few gaps so it never reads as wallpaper
        this.place(row, col);
      }
    }
    this.rowsBuilt = Math.max(this.rowsBuilt, rowsNeeded);
  }

  place(row, col) {
    const doodle = this.nextDoodle();
    const sheet = this.config.sheets[doodle.sheet];
    if (!sheet) return;

    const [fx, fy, fw, fh] = doodle.crop;
    const boxW = this.baseSize * doodle.scale * (0.85 + Math.random() * 0.3);
    const boxH = boxW * (fh * sheet.h) / (fw * sheet.w);
    const sheetScale = boxW / (fw * sheet.w); // displayed px per sheet px
    const x = col * this.cellW + this.cellW * (0.2 + Math.random() * 0.6) - boxW / 2;
    const y = row * this.cell + this.cell * (0.2 + Math.random() * 0.6) - boxH / 2;
    const tilt = (Math.random() - 0.5) * 24;

    const wrapper = document.createElement('div');
    wrapper.className = 'absolute';
    wrapper.style.cssText = `left:${x.toFixed(1)}px;top:${y.toFixed(1)}px;width:${boxW.toFixed(1)}px;height:${boxH.toFixed(1)}px;rotate:${tilt.toFixed(1)}deg;opacity:var(--doodle-opacity,0.14)`;

    const floater = document.createElement('div');
    floater.className = 'size-full';
    const actor = document.createElement('div');
    actor.className = 'size-full bg-no-repeat';
    actor.style.backgroundImage = `url("${sheet.url}")`;
    actor.style.backgroundSize = `${(sheet.w * sheetScale).toFixed(1)}px ${(sheet.h * sheetScale).toFixed(1)}px`;
    actor.style.backgroundPosition = `${(-fx * sheet.w * sheetScale).toFixed(1)}px ${(-fy * sheet.h * sheetScale).toFixed(1)}px`;

    floater.appendChild(actor);
    wrapper.appendChild(floater);
    this.layer.appendChild(wrapper);

    if (this.reducedMotion.matches) return;
    const record = { element: actor, moves: doodle.moves || Object.keys(ACTIONS), float: this.float(floater) };
    record.float.pause();
    wrapper.doodleActor = record;
    this.actors.push(record);
    this.observer.observe(wrapper);
  }

  float(element) {
    const range = 14;
    const dx = (Math.random() - 0.5) * 2 * range;
    const dy = (Math.random() - 0.5) * 2 * range;
    const scaleTo = 0.9 + Math.random() * 0.2;
    const duration = 5000 + Math.random() * 6000;
    return element.animate(
      [
        { transform: 'translate(0, 0) scale(1)' },
        { transform: `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px) scale(${scaleTo.toFixed(3)})` },
      ],
      { duration, delay: -Math.random() * duration, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out' }
    );
  }

  // Each tick, a couple of random on-screen doodles perform one of their actions.
  startActions() {
    if (this.timer || !this.actors.length || this.reducedMotion.matches || document.hidden) return;
    const tick = () => {
      const visible = [...this.visibleActors];
      if (visible.length) {
        const count = 1 + Math.floor(Math.random() * Math.max(2, visible.length / 8));
        for (let i = 0; i < count; i++) {
          const target = visible[Math.floor(Math.random() * visible.length)];
          if (target.busy) continue;
          const name = target.moves[Math.floor(Math.random() * target.moves.length)];
          const action = ACTIONS[name];
          if (!action) continue;
          target.busy = true;
          target.element.style.transformOrigin = action.origin || '50% 60%';
          const animation = target.element.animate(action.keyframes, {
            duration: action.duration * (0.85 + Math.random() * 0.3),
            easing: action.easing || 'ease-in-out',
          });
          animation.onfinish = animation.oncancel = () => (target.busy = false);
        }
      }
      this.timer = setTimeout(tick, 250 + Math.random() * 300);
    };
    this.timer = setTimeout(tick, 400);
  }

  stopActions() {
    clearTimeout(this.timer);
    this.timer = undefined;
  }
}

if (!customElements.get('s-doodle-field')) customElements.define('s-doodle-field', SDoodleField);
